import type { LayerState } from '@vitals/shared';
import type { Vital, VitalCluster, VitalView } from './vitals.js';
// Cycle with vitals.ts (it imports commuterRailCluster into VITALS). Safe
// because these bindings are only read inside deferred select() closures —
// never at module-eval time. Keep it that way: a module-scope read of a
// vitals.js export here could hit the TDZ depending on which module a given
// entry point (app, test, bundler) evaluates first.
import { activeTrains } from './vitals.js';
import { vitalIcon } from './icons.js';

/** One commuter railroad's child vital (rendered when the cluster is expanded). */
function railVital(id: string, label: string): Vital {
  return {
    id,
    layerId: id,
    group: 'transit',
    icon: vitalIcon('rail'),
    select: (s): VitalView => {
      const trains = activeTrains(s);
      const down = trains === null;
      const stale = s?.status === 'stale';
      return {
        value: down ? '—' : trains.toLocaleString('en-US'),
        label,
        down,
        stale,
      };
    },
  };
}

export const lirrVital: Vital = railVital('lirr', 'LIRR');
export const mnrVital: Vital = railVital('mnr', 'Metro-North');
export const pathVital: Vital = railVital('path', 'PATH');

/**
 * The Transit group's trains cluster: one chip totaling LIRR + MNR + PATH
 * trains, expandable into the three child vitals. Down only when ALL are
 * down (one railroad's outage shouldn't blank the others' counts); stale when
 * any is.
 */
export const commuterRailCluster: VitalCluster = {
  id: 'commuter',
  group: 'transit',
  icon: vitalIcon('rail'),
  layerIds: ['lirr', 'mnr', 'path'],
  children: [lirrVital, mnrVital, pathVital],
  select: (states): VitalView => {
    const counts = states.map(activeTrains);
    const live = counts.filter((c): c is number => c !== null);
    const down = live.length === 0;
    const stale = states.some((s) => s?.status === 'stale');
    const total = live.reduce((a, b) => a + b, 0);
    return {
      value: down ? '—' : total.toLocaleString('en-US'),
      label: 'trains',
      down,
      stale,
    };
  },
};
